import React from 'react';

import { usePhotoFileInput } from '../hooks/usePhotoFileInput';
import { PhotoUploadIcon } from './PhotoUploadIcon';
import * as S from '../PhotoPicker.styles';

interface Props {
  acceptedFileTypes?: readonly string[];
  /** Short label used on the filled trigger's aria-label. */
  addMoreLabel: string;
  editTileLabel: string;
  emptyLabel: string;
  isUploading?: boolean;
  maxFileSizeBytes?: number;
  maxFiles?: number;
  /** Fires when the customer clicks the filled-state trigger (i.e. to open
   * the drawer). Not invoked for empty-state clicks — those open the OS
   * file picker via `onFilesSelected`. */
  onClick: () => void;
  onFilesRejected?: (rejected: { oversized: File[]; wrongType: File[] }) => void;
  /** Called with files picked or dropped on the empty trigger. */
  onFilesSelected: (files: File[]) => void;
  previewFor: (url: string) => string | undefined;
  triggerLabel?: string;
  uploadingLabel?: string;
  urls: string[];
}

function chunkPairs<T>(arr: T[]): T[][] {
  const pairs: T[][] = [];
  for (let i = 0; i < arr.length; i += 2) {
    pairs.push(arr.slice(i, i + 2));
  }
  return pairs;
}

const MINI_GRID_VISIBLE = 4;

export function PhotoPickerTrigger({
  acceptedFileTypes,
  addMoreLabel,
  editTileLabel,
  emptyLabel,
  isUploading,
  maxFileSizeBytes,
  maxFiles,
  onClick,
  onFilesRejected,
  onFilesSelected,
  previewFor,
  triggerLabel,
  uploadingLabel,
  urls,
}: Props) {
  // Empty-state click and drag-drop go directly to the OS file picker so the
  // customer can upload without opening the drawer. Filled-state clicks fall
  // back to the consumer-provided onClick (which opens the drawer for edits).
  const { dropzoneProps, inputProps } = usePhotoFileInput({
    acceptedFileTypes,
    maxFileSizeBytes,
    maxFiles,
    onFilesRejected,
    onFilesSelected,
  });

  if (urls.length === 0) {
    return (
      <S.TriggerEmpty {...dropzoneProps} disabled={isUploading} type="button">
        <PhotoUploadIcon />
        <span>{isUploading && uploadingLabel ? uploadingLabel : emptyLabel}</span>
        <input {...inputProps} data-testid="phototrigger-file-input" />
      </S.TriggerEmpty>
    );
  }

  const editTile = (
    <S.TriggerAddMoreTile aria-hidden="true">
      <PhotoUploadIcon />
      <span>{editTileLabel}</span>
    </S.TriggerAddMoreTile>
  );

  const renderImg = (url: string) => <img alt="" key={url} src={previewFor(url) ?? url} />;

  // For 4+ photos: collapse the remainder into a 2x2 mini-grid in row 2 (left)
  // beside the edit tile (right), so the trigger stays a fixed height regardless
  // of how many photos the user uploads. 1–3 photos render as big tiles in
  // pair rows.
  const renderGridContents = () => {
    if (urls.length >= 4) {
      const remaining = urls.slice(2);
      const miniUrls = remaining.slice(0, MINI_GRID_VISIBLE);
      const overflow = remaining.length - MINI_GRID_VISIBLE;
      return (
        <>
          <S.TriggerFilledRow>{urls.slice(0, 2).map(renderImg)}</S.TriggerFilledRow>
          <S.TriggerFilledRow>
            <S.TriggerMiniGrid>
              {miniUrls.map((url, i) => (
                <S.TriggerMiniCell key={url}>
                  <img alt="" src={previewFor(url) ?? url} />
                  {i === MINI_GRID_VISIBLE - 1 && overflow > 0 && (
                    <S.TriggerOverflowBadge>+{overflow}</S.TriggerOverflowBadge>
                  )}
                </S.TriggerMiniCell>
              ))}
            </S.TriggerMiniGrid>
            {editTile}
          </S.TriggerFilledRow>
        </>
      );
    }

    const rows = chunkPairs(urls);
    const lastRowFull = rows[rows.length - 1].length === 2;
    return (
      <>
        {rows.map((pair, rowIdx) => {
          const isLastRow = rowIdx === rows.length - 1;
          return (
            <S.TriggerFilledRow key={rowIdx}>
              {pair.map(renderImg)}
              {isLastRow && !lastRowFull && editTile}
            </S.TriggerFilledRow>
          );
        })}
        {lastRowFull && <S.TriggerFilledRow>{editTile}</S.TriggerFilledRow>}
      </>
    );
  };

  return (
    <S.TriggerFilled
      aria-label={`${addMoreLabel} — ${urls.length} photo${urls.length === 1 ? '' : 's'} added`}
      onClick={onClick}
      type="button"
    >
      {triggerLabel && <S.TriggerLabel>{triggerLabel}</S.TriggerLabel>}
      <S.TriggerFilledGrid>{renderGridContents()}</S.TriggerFilledGrid>
    </S.TriggerFilled>
  );
}
